import { Artist, Location } from "../../types/Artist";
import { Album } from "../../types/Album";
import { FileResponse } from "../../types/FileResponse";
import { Track } from "../../types/Track";
import { TokenResponse } from "../../types/TokenResponse";
import { Genre } from "../../types/Genre";
import { Recording } from "../../types/Recording";
import { Compilation } from "../../types/Compilation";
import { CompilationTrack } from "../../types/CompilationTrack";
import { ArtistMessage } from "../../types/ArtistMessage";
import { postAlbumRequest } from "../../types/messages/postAlbumRequest";
import { postTrackRequest } from "../../types/messages/postTrackRequest";
import { postCompilationTrackRequest } from "../../types/messages/postCompilationTrackRequest";
import { deleteRequest, getRequest, postAuthorizeCode, postFormRequest, postRequest } from "./requests";
import { HistoryTrack } from "../../types/HistoryTrack";
import { SearchResults } from "../../types/SearchResults";
import { AnalyticsCounts } from "../../types/AnalyticsCounts";
import { Playlist } from "../../types/Playlist";
import { AddPlaylistItem } from "../../types/AddPlaylistItem";
import { postSearchForArtistRequest } from "../../types/messages/PostSearchForArtistRequest";
import { ArtistVector } from "../../types/ArtistVector";
import { QueueResponse } from "../../types/QueueResponse";
import { LyricsResponse } from "../../types/LyricsResponse";

// ===================================
// Ai
// ===================================
export const postGenerateEmbeddings = async (accessToken: string): Promise<boolean> =>
    { return await postRequest<boolean>(`/Ai/Embeddings/Generate`, accessToken, undefined); }

export const postVectorSearchArtist = async (accessToken: string, data: postSearchForArtistRequest): Promise<Artist[]> =>
    { return await postRequest<Artist[]>(`/Ai/Search/Artists`, accessToken, data); }

export const getArtistVector = async (accessToken: string, artistName: string): Promise<ArtistVector> =>
    { return await getRequest<ArtistVector>(`/Ai/Vectors/Artists/${artistName}`, accessToken); }

export const postArtistVector = async (accessToken: string, data: ArtistVector): Promise<boolean> =>
    { return await postRequest<boolean>(`/Ai/Vectors/Artists/${data.artistName}`, accessToken, data); }

// ===================================
// Authorization
// ===================================
export const postAuthorize = async (authenticationCode: string): Promise<TokenResponse> =>
    { return await postAuthorizeCode(authenticationCode); }

// ===================================
// Artists
// ===================================
export const getLetters = async (accessToken: string): Promise<string[]> =>
    { return await getRequest<string[]>('/Letters', accessToken); }

export const getAllArtists = async (accessToken: string): Promise<string[]> =>
    { return await getRequest<string[]>('/Artists', accessToken); }

export const getArtistsByGroupName = async (accessToken: string, groupName: string): Promise<ArtistMessage[]> =>
    { return await getRequest<ArtistMessage[]>(`/Letters/${groupName}/Artists`, accessToken); }

export const getArtist = async (accessToken: string, artistName: string): Promise<Artist> =>
    { return await getRequest<Artist>(`/Artists/${artistName}`, accessToken); }

export const deleteArtist = async (accessToken: string, artistName: string): Promise<number> =>
    { return await deleteRequest<number>(`/Artists/${artistName}`, accessToken); }

export const postArtist = async (accessToken: string, artistName: string, data: Artist): Promise<boolean> =>
    { return await postFormRequest<boolean>(`/Artists/${artistName}`, accessToken, data); }

export const postArtistRelations = async (accessToken: string, artistName: string, data: readonly string[]): Promise<boolean> =>
    { return await postRequest<boolean>(`/Artists/${artistName}/Related`, accessToken, data); }

export const getArtistPhotos = async (accessToken: string, artistName: string, rating: string): Promise<string[]> =>
    { return await getRequest<string[]>(`/Artists/${artistName}/Photos?rating=${rating}`, accessToken); }

export const getArtistPhoto = async (accessToken: string, artistName: string, photoName: string): Promise<FileResponse> =>
    { return await getRequest<FileResponse>(`/Artists/${artistName}/Photo/${photoName}`, accessToken); }

export const postArtistPhotoRatingChange = async (accessToken: string, artistName: string, photoName: string, rating: string): Promise<boolean> =>
    { return await postRequest<boolean>(`/Artists/${artistName}/PhotoChange/${photoName}?rating=${rating}`, accessToken, {}); }

export const getArtistLogo = async (accessToken: string, artistName: string): Promise<FileResponse> =>
    { return await getRequest<FileResponse>(`/Artists/${artistName}/Logo`, accessToken); }

export const deleteArtistLogo = async (accessToken: string, artistName: string): Promise<boolean> =>
    { return await deleteRequest<boolean>(`/Artists/${artistName}/Logo`, accessToken); }

export const postArtistLogo = async (accessToken: string, artistName: string, data: FormData): Promise<FileResponse> =>
    { return await postRequest<FileResponse>(`/Artists/${artistName}/Logo`, accessToken, data); }

export const deleteArtistPhoto = async (accessToken: string, artistName: string, photoName: string): Promise<boolean> =>
    { return await deleteRequest<boolean>(`/Artists/${artistName}/Photo/${photoName}`, accessToken); }

export const postArtistPhoto = async (accessToken: string, artistName: string, rating: string, data: FormData): Promise<FileResponse> =>
    { return await postRequest<FileResponse>(`/Artists/${artistName}/Photo/${rating}`, accessToken, data); }

export const getArtistsByLocation = async (accessToken: string, location: Location): Promise<ArtistMessage[]> =>
    { return await postRequest<ArtistMessage[]>(`/Artists/Location`, accessToken, location); }

// ===================================
// Albums
// ===================================
export const getAlbums = async (accessToken: string, artistName: string): Promise<Album[]> =>
    { return await getRequest<Album[]>(`/Artists/${artistName}/Albums`, accessToken); }

export const getAlbum = async (accessToken: string, artistName: string, albumName: string): Promise<Album> =>
    { return await getRequest<Album>(`/Artists/${artistName}/Albums/${albumName}`, accessToken); }

export const getPreviousAlbum = async (accessToken: string, artistName: string, albumName: string): Promise<Album> =>
    { return await getRequest<Album>(`/Artists/${artistName}/Albums/${albumName}/Previous`, accessToken); }

export const getNextAlbum = async (accessToken: string, artistName: string, albumName: string): Promise<Album> =>
    { return await getRequest<Album>(`/Artists/${artistName}/Albums/${albumName}/Next`, accessToken); }

export const getAlbumCover = async (accessToken: string, artistName: string, albumName: string): Promise<FileResponse> =>
    { return await getRequest<FileResponse>(`/Artists/${artistName}/Albums/${albumName}/Cover`, accessToken); }

export const getAlbumCoverWithoutCache = async (accessToken: string, artistName: string, albumName: string): Promise<FileResponse> =>
    { return await getRequest<FileResponse>(`/Artists/${artistName}/Albums/${albumName}/CoverWithoutCache`, accessToken); }

export const getAlbumThumbnail = async (accessToken: string, artistName: string, albumName: string): Promise<FileResponse> =>
    { return await getRequest<FileResponse>(`/Artists/${artistName}/Albums/${albumName}/Thumbnail`, accessToken); }

export const getAlbumThumbnailWithToken = async (accessToken: string, artistName: string, albumName: string): Promise<FileResponse> =>
    { return await getRequest<FileResponse>(`/Stream/Artists/${artistName}/Albums/${albumName}/Thumbnail?accessToken=${accessToken}`, accessToken); }

export const postAlbumImage = async (accessToken: string, artistName: string, albumName: string, fileName: string, data: FormData): Promise<FileResponse> =>
    { return await postRequest<FileResponse>(`/Artists/${artistName}/Albums/${albumName}/Image/${fileName}`, accessToken, data); }

export const postAlbum = async (accessToken: string, artistName: string, albumName: string, data: postAlbumRequest): Promise<boolean> =>
    { return await postFormRequest<boolean>(`/Artists/${artistName}/Albums/${albumName}`, accessToken, data); }

export const postAlbumRating = async (accessToken: string, artistName: string, albumName: string, rating: string): Promise<boolean> =>
    { return await postRequest<boolean>(`/Artists/${artistName}/Albums/${albumName}/Rating/${rating}`, accessToken, {}); }

// ===================================
// Compilations
// ===================================
export const getCompilationCover = async (accessToken: string, directory: string, albumName: string): Promise<FileResponse> =>
    { return await getRequest<FileResponse>(`/Compilations/${directory}/Albums/${albumName}/Cover`, accessToken); }

export const getCompilationThumbnail = async (accessToken: string, directory: string, albumName: string): Promise<FileResponse> =>
    { return await getRequest<FileResponse>(`/Compilations/${directory}/Albums/${albumName}/Thumbnail`, accessToken); }

export const getCompilationThumbnailWithToken = async (accessToken: string, directory: string, albumName: string): Promise<FileResponse> =>
    { return await getRequest<FileResponse>(`/Stream/Compilations/${directory}/Albums/${albumName}/Thumbnail?accessToken=${accessToken}`, accessToken); }

export const postCompilationImage = async (accessToken: string, directory: string, albumName: string, fileName: string, data: FormData): Promise<FileResponse> =>
    { return await postRequest<FileResponse>(`/Compilations/${directory}/Albums/${albumName}/Image/${fileName}`, accessToken, data); }

export const postCompilation = async (accessToken: string, directory: string, albumName: string, data: FormData): Promise<FileResponse> =>
    { return await postRequest<FileResponse>(`/Compilations/${directory}/Albums/${albumName}`, accessToken, data); }

export const postCompilationRating = async (accessToken: string, directory: string, albumName: string, rating: string): Promise<boolean> =>
    { return await postRequest<boolean>(`/Compilations/${directory}/Albums/${albumName}/Rating/${rating}`, accessToken, {}); }

export const postCompilationTrack = async (accessToken: string, directory: string, albumName: string, trackName: string, data: postCompilationTrackRequest): Promise<boolean> =>
    { return await postFormRequest<boolean>(`/Compilations/${directory}/Albums/${albumName}/Tracks/${trackName}`, accessToken, data); }

export const postCompilationIncrementTrackListenCount = async (accessToken: string, directory: string, albumName: string, trackName: string): Promise<boolean> =>
    { return await postFormRequest<boolean>(`/Compilations/${directory}/Albums/${albumName}/Tracks/${trackName}/IncrementListenCount`, accessToken, null); }

export const getCompilations = async (accessToken: string): Promise<{ directory: string, count: number }[]> =>
    { return await getRequest<{ directory: string, count: number }[]>(`/Compilations`, accessToken); }

export const getCompilationsByArtist = async (accessToken: string, artistName: string): Promise<Compilation[]> =>
    { return await getRequest<Compilation[]>(`/Artists/${artistName}/Compilations`, accessToken); }

export const getCompilationsByDirectory = async (accessToken: string, directory: string): Promise<Compilation[]> =>
    { return await getRequest<Compilation[]>(`/Compilations/${directory}`, accessToken); }

export const getCompilation = async (accessToken: string, directory: string, albumName: string): Promise<Compilation> =>
    { return await getRequest<Compilation>(`/Compilations/${directory}/Albums/${albumName}`, accessToken); }

export const getCompilationTracks = async (accessToken: string, directory: string, albumName: string): Promise<CompilationTrack[]> =>
    { return await getRequest<CompilationTrack[]>(`/Compilations/${directory}/Albums/${albumName}/Tracks`, accessToken); }

// ===================================
// Tracks
// ===================================
export const getTracks = async (accessToken: string, artistName: string, albumName: string): Promise<Track[]> => { 
    const tracks: Track[] = await getRequest<Track[]>(`/Artists/${artistName}/Albums/${albumName}/Tracks`, accessToken);
    return tracks.map((track: Track) => ({ number: track.number, name: track.name, duration: track.duration, listenCount: track.listenCount }));
}

export const postTrack = async (accessToken: string, artistName: string, albumName: string, trackName: string, data: postTrackRequest): Promise<boolean> =>
    { return await postFormRequest<boolean>(`/Artists/${artistName}/Albums/${albumName}/Tracks/${trackName}`, accessToken, data); }

export const postIncrementTrackListenCount = async (accessToken: string, artistName: string, albumName: string, trackName: string): Promise<boolean> =>
    { return await postFormRequest<boolean>(`/Artists/${artistName}/Albums/${albumName}/Tracks/${trackName}/IncrementListenCount`, accessToken, null); }

// ===================================
// Admin
// ===================================
export const getApplyToken = async (accessToken: string): Promise<void> =>
    { return await getRequest<void>(`/Authentication/ApplyToken`, accessToken); }

export const getRepairSimilarArtistName = async (accessToken: string, oldName: string, newName: string): Promise<string[]> =>
    { return await getRequest<string[]>(`/Admin/RepairSimilarArtistName?wrongName=${oldName}&correctName=${newName}`, accessToken); }
   
export const getSyncronizeArtistData = async (accessToken: string, artistName: string): Promise<number> =>
    { return await getRequest<number>(`/Admin/SyncronizeArtistData/Artist/${artistName}`, accessToken); }

export const getSyncronizeCompilationData = async (accessToken: string): Promise<number> =>
    { return await getRequest<number>(`/Admin/SyncronizeCompilationData`, accessToken); }

export const getAddTrackDurationDataAsync = async (accessToken: string): Promise<number> =>
    { return await getRequest<number>(`/Admin/AddTrackDurationDataAsync`, accessToken); }

export const getAddArtistDataAsync = async (accessToken: string): Promise<number> =>
    { return await getRequest<number>(`/Admin/AddArtistData`, accessToken); }

export const fixSimilarArtistsAsync = async (accessToken: string): Promise<number> =>
    { return await getRequest<number>(`/Admin/FixSimilarArtists`, accessToken); }

export const runDataCleanupAsync = async (accessToken: string): Promise<number> =>
    { return await getRequest<number>(`/Admin/RunDataCleanup`, accessToken); }

// ===================================
// Random
// ===================================
export const getRandomArtistPhoto = async (accessToken: string, artistName: string, rating: string): Promise<FileResponse> =>
    { return await getRequest<FileResponse>(`/Artists/${artistName}/Photo/Random?rating=${rating}`, accessToken); }

export const getRandomRecordings = async (accessToken: string, size: number, maxDuration: number): Promise<Recording[]> =>
    { return await getRequest<Recording[]>(`/RandomRecordings/${size}?maxDuration=${maxDuration}`, accessToken); }

export const getRandomRecordingsEmphasizingArtists = async (accessToken: string, size: number, maxDuration: number): Promise<Recording[]> =>
    { return await getRequest<Recording[]>(`/RandomRecordings/${size}/EmphasizingArtists?maxDuration=${maxDuration}`, accessToken); }

export const getRandomRecordingsByGenre = async (accessToken: string, size: number, maxDuration: number, genreId: number): Promise<Recording[]> =>
    { return await getRequest<Recording[]>(`/RandomRecordings/${size}/Genre/${genreId}?maxDuration=${maxDuration}`, accessToken); }

export const getRandomRecordingsByArtist = async (accessToken: string, size: number, maxDuration: number, artistName: string): Promise<Recording[]> =>
    { return await getRequest<Recording[]>(`/RandomRecordings/${size}/Artist/${artistName}?maxDuration=${maxDuration}`, accessToken); }

export const getRandomRecordingsBySimilarArtist = async (accessToken: string, size: number, maxDuration: number, artistName?: string): Promise<Recording[]> =>
    { return await getRequest<Recording[]>(`/RandomRecordings/${size}/SimilarArtist/${artistName}?maxDuration=${maxDuration}`, accessToken); }

// ===================================
// Recommendations
// ===================================
export const getSuggestion = async (accessToken: string): Promise<Recording> =>
    { return await getRequest<Recording>(`/Recommendations`, accessToken); }

// ===================================
// Genres
// ===================================
export const getGenre = async (accessToken: string, genreId: string): Promise<Genre> =>
    { return await getRequest<Genre>(`/Genres/${genreId}`, accessToken); }

export const postGenre = async (accessToken: string, data: Genre): Promise<Genre> =>
    { return await postRequest<Genre>(`/Genres/${data.id}`, accessToken, data); }

export const deleteGenre = async (accessToken: string, genreId: number): Promise<boolean> =>
    { return await getRequest<boolean>(`/Genres/${genreId}`, accessToken); }

export const getArtistsByGenre = async (accessToken: string, genreId: string): Promise<ArtistMessage[]> =>
    { return await getRequest<ArtistMessage[]>(`/Genres/${genreId}/Artists`, accessToken); }

export const getGenres = async (accessToken: string): Promise<Genre[]> =>
    { return await getRequest<Genre[]>(`/Genres`, accessToken); }

export const getGenreNextId = async (accessToken: string): Promise<number> =>
    { return await getRequest<number>(`/Genres/NextId`, accessToken); }

export const postArtistGenres = async (accessToken: string, artistName: string, data: readonly number[]): Promise<boolean> =>
    { return await postRequest<boolean>(`/Artists/${artistName}/Genres`, accessToken, data); }

// ===================================
// History
// ===================================
export const getRecentTracks = async (accessToken: string, limit: number): Promise<HistoryTrack[]> =>
    { return await getRequest<HistoryTrack[]>(`/History/RecentTracks/${limit}`, accessToken); }

// ===================================
// Search
// ===================================
export const postSearch = async (accessToken: string, searchText: string): Promise<SearchResults> =>
    { return await postRequest<SearchResults>('/Search', accessToken, { searchText }); }

// ===================================
// Analytics
// ===================================
export const getAnalyticsCounts = async (accessToken: string): Promise<AnalyticsCounts> =>
    { return await getRequest<AnalyticsCounts>('/Analytics/Counts', accessToken); }

// ===================================
// Playlists
// ===================================
export const getPlaylists = async (accessToken: string): Promise<Playlist[]> =>
    { return await getRequest<Playlist[]>(`/Playlists`, accessToken); }

export const postPlaylist = async (accessToken: string, data: Playlist): Promise<Playlist> =>
    { return await postRequest<Playlist>(data.id ? `/Playlists/${data.id}` : `/Playlists`, accessToken, data); }

export const getPlaylist = async (accessToken: string, playlistId: string): Promise<Playlist> =>
    { return await getRequest<Playlist>(`/Playlists/${playlistId}`, accessToken); }

export const deletePlaylist = async (accessToken: string, playlistId: string): Promise<boolean> =>
    { return await deleteRequest<boolean>(`/Playlists/${playlistId}`, accessToken); }

export const postPlaylistItem = async (accessToken: string, playlistId: string, data: AddPlaylistItem): Promise<Playlist> =>
    { return await postRequest<Playlist>(`/Playlists/${playlistId}/Item`, accessToken, data); }

export const deletePlaylistItem = async (accessToken: string, playlistId: string, number: number): Promise<boolean> =>
    { return await deleteRequest<boolean>(`/Playlists/${playlistId}/Number/${number}`, accessToken); }

export const postPlaylistItemShift = async (accessToken: string, playlistId: string, number: number, direction: "up" | "down"): Promise<boolean> =>
    { return await postRequest<boolean>(`/Playlists/${playlistId}/Number/${number}/Direction/${direction}`, accessToken, null); }

// ===================================
// Queue
// ===================================
export const getQueue = async (accessToken: string): Promise<Recording[]> =>
    { return await getRequest<Recording[]>('/Queue', accessToken); }

export const getCurrentQueueItem = async (accessToken: string): Promise<QueueResponse> =>
    { return await getRequest<QueueResponse>('/Queue/Current', accessToken); }

export const postQueue = async (accessToken: string, recordings: Recording[]): Promise<boolean> =>
    { return await postRequest<boolean>(`/Queue`, accessToken, recordings); }

export const postRemoveRecordingFromQueue = async (accessToken: string, recording: Recording): Promise<boolean> =>
    { return await postRequest<boolean>(`/Queue/Remove`, accessToken, recording); }

// ===================================
// Lyrics
// ===================================
export const getLyrics = async (accessToken: string, artistName: string, title: string): Promise<LyricsResponse> =>
    { return await getRequest<LyricsResponse>(`/Lyrics/Artist/${artistName}/Title/${title}`, accessToken); }